import { Empty, List, Tag } from 'antd';

import { getAssetTypeName, getAssetTypeTagColor, getAssetTypeTagStyle } from '@/shared/api/openmetadata';
import type { AssetItem, AssetListProps, BaseAssetItem } from '@/shared/types/assetList';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';

import styles from './AssetList.module.css';

export const AssetList = <T extends BaseAssetItem = AssetItem>({
  items,
  getActions,
  getMetadata,
  metadataIcon,
  onItemClick,
}: AssetListProps<T>) => {
  const handleKeyDown = (event: React.KeyboardEvent, item: T) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onItemClick?.(item);
    }
  };

  return (
    <div className={styles.listWrapper}>
      {items.length > 0 ? (
        <List
          className={styles.list}
          dataSource={items}
          renderItem={(item) => {
            const typeTagColor = getAssetTypeTagColor(item.type);
            const typeTagCustomStyle = getAssetTypeTagStyle(item.type);
            const typeName = getAssetTypeName(item.type);
            const actions = getActions(item);

            return (
              <List.Item
                className={styles.listItem}
                onClick={() => onItemClick?.(item)}
                onKeyDown={(e) => handleKeyDown(e, item)}
                tabIndex={0}
                role="button"
                aria-label={`${item.name} - ${typeName}`}
                actions={actions.map((action) => (
                  <button
                    key={action.key}
                    className={action.className}
                    style={action.style}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick();
                    }}>
                    {action.icon}
                  </button>
                ))}>
                <List.Item.Meta
                  title={
                    <div className={styles.itemTitle}>
                      <div className={styles.titleLeft}>
                        <Tag
                          color={typeTagCustomStyle ? undefined : typeTagColor}
                          style={typeTagCustomStyle || undefined}
                          className={styles.typeTag}>
                          <Icon assetType={item.type} size={13} />
                          <span>{typeName}</span>
                        </Tag>
                        <EllipsisText text={item.name} className={styles.itemName} />
                      </div>
                    </div>
                  }
                  description={
                    <div className={styles.itemDescription}>
                      <div className={styles.metadata}>
                        {metadataIcon || <Icon name="clock" size={12} />}
                        <span>{getMetadata(item)}</span>
                      </div>
                    </div>
                  }
                />
              </List.Item>
            );
          }}
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="자산이 없습니다" />
      )}
    </div>
  );
};
